// Gemensam hantering av revisionskolumner (person, skede, kommentar/status)
// så att alla Word- och tabellexporter ser likadana ut.
import type { Table } from "docx";
import { table, h2, body } from "@/lib/documentGenerator/docTheme";
import { formatFieldValue, getFieldLabel } from "@/utils/revisionTracking";
import { getActivePhase } from "@/hooks/useCurrentPhase";

export interface RevisionEntry {
  id?: string;
  timestamp: string;
  userName?: string;
  phaseKey?: string | null;
  phaseName?: string;
  changes?: Array<{ field: string; oldValue: any; newValue: any }>;
}

/** Rubriker som ska användas i alla revisionstabeller. */
export const REVISION_HEADERS = ["Datum", "Ändrad av", "Skede", "Kommentar/Status"] as const;

/** Kolumnrubriker när revisionen visas som extra kolumner i en lista. */
export const REVISION_LIST_HEADERS = ["Ändrad av", "Skede", "Kommentar/Status"] as const;

export const formatRevisionDate = (value?: string | null): string =>
  value ? new Date(value).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" }) : "–";

export const revisionPhaseName = (rev?: { phaseName?: string; phaseKey?: string | null }): string =>
  rev?.phaseName || rev?.phaseKey || "Ej angivet";

/** Aktuellt skede – används när posten saknar fryst skede. */
export const currentPhaseName = (): string => getActivePhase().name || "Ej angivet";

export const revisionChangeSummary = (rev: RevisionEntry): string =>
  (rev.changes ?? [])
    .map((c) => `${getFieldLabel(c.field)}: ${formatFieldValue(c.oldValue)} → ${formatFieldValue(c.newValue)}`)
    .join("; ") || "Ingen ändringsdetalj";

/** En rad per revision: datum, person, skede, kommentar/status. */
export const revisionRow = (rev: RevisionEntry, note?: string): string[] => [
  formatRevisionDate(rev.timestamp),
  rev.userName || "Okänd",
  revisionPhaseName(rev),
  note ?? revisionChangeSummary(rev),
];

/** Extra listkolumner (person, skede, kommentar/status) för senaste revisionen. */
export const revisionListCells = (
  revisions: RevisionEntry[] | undefined,
  fallback?: { userName?: string | null; phaseName?: string | null; note?: string | null }
): string[] => {
  const latest = revisions?.length ? revisions[revisions.length - 1] : undefined;
  if (!latest) {
    return [
      fallback?.userName || "–",
      fallback?.phaseName || currentPhaseName(),
      fallback?.note || "–",
    ];
  }
  return [
    latest.userName || fallback?.userName || "Okänd",
    revisionPhaseName(latest),
    fallback?.note || revisionChangeSummary(latest),
  ];
};

/** Standardtabell för revisionshistorik. */
export const revisionTable = (rows: string[][], opts: { columnWidths?: number[] } = {}): Table =>
  table([...REVISION_HEADERS], rows, {
    columnWidths: opts.columnWidths ?? [1600, 2000, 1800, 3960],
  });

/** Färdigt avsnitt: rubrik, ingress och tabell. */
export const revisionSection = (
  rows: string[][],
  opts: { title?: string; intro?: string } = {}
) => [
  h2(opts.title ?? "Revisionshistorik"),
  body(
    opts.intro ??
      "Loggen visar vem som ändrat, i vilket skede ändringen gjordes samt kommentar/status för ändringen."
  ),
  revisionTable(rows),
];
